import { Option } from '@/components/TeamAndSchedule/types/facetsTypes';
import { dateTimeFormatterByTimezone } from '@/utils/formatter';
import { FacetData, FacetOptions } from '@/components/TeamAndSchedule/types/facetsTypes';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { loadFacetsFromSession } from './filterSessionStorage';

const logger = useLogger('TeamSchedule -> facetUtils');

export const getBoundaries = (options: Option[]): string => {
    if (options.length === 0) return '';
    const minValue = options[0].start;
    const maxValue = options[options.length - 1].end;

    return `${minValue}_${maxValue}`;
};

export const formatDateWithTimezone = (dateStr: string | undefined, userTimezone: string): Date | undefined => {
    const formattedDate = dateStr ? dateTimeFormatterByTimezone(dateStr, 'yyyy/MM/dd', userTimezone) : undefined;
    return formattedDate ? new Date(formattedDate) : undefined;
};

const mergeSubBuckets = (
    prevSubBuckets: FacetOptions[] = [],
    newSubBuckets: FacetOptions[] = []
): FacetOptions[] => {
    const newSubBucketMap = new Map(newSubBuckets.map((subBucket) => [ subBucket.id, subBucket ]));

    const updatedSubBuckets = prevSubBuckets.map((prev) => {
        const newMatch = newSubBucketMap.get(prev.id);
        return newMatch
            ? { ...prev, count: newMatch.count }
            : { ...prev, count: 0 };
    });

    const existingIds = new Set(prevSubBuckets.map((subBucket) => subBucket.id));
    const newOnes = newSubBuckets.filter((subBucket) => !existingIds.has(subBucket.id));

    return [ ...updatedSubBuckets, ...newOnes ];
};

const mergeSingleOption = (
    currentOption?: FacetOptions,
    newOption?: FacetOptions
): FacetOptions => {
    const hasSubBuckets =
        newOption?.subBuckets?.length || currentOption?.subBuckets?.length;

    const mergedOption: FacetOptions = {
        ...(newOption || currentOption)!,
        count: newOption?.count ?? 0
    };

    if (hasSubBuckets) {
        mergedOption.subBuckets = mergeSubBuckets(
            currentOption?.subBuckets,
            newOption?.subBuckets
        );
    }

    return mergedOption;
};

export const mergeFacets = (
    facetName: string,
    currentData: Record<string, FacetData> = {},
    newData: Record<string, FacetData> = {}
) => {
    const currentOptions = currentData[facetName]?.options ?? [];
    const newOptions = newData[facetName]?.options ?? [];

    const mergedOptions: FacetOptions[] = [];
    const currentMap = new Map(currentOptions.map((option) => [ option.id, option ]));
    const newMap = new Map(newOptions.map((option) => [ option.id, option ]));

    newMap.forEach((newOption, id) => {
        const currentOption = currentMap.get(id);
        mergedOptions.push(mergeSingleOption(currentOption, newOption));
    });

    currentMap.forEach((currentOption, id) => {
        if (!newMap.has(id)) {
            mergedOptions.push(mergeSingleOption(currentOption, undefined));
        }
    });

    return {
        facetName,
        options: mergedOptions
    };
};

export const mixCurrentWithNewData = (currentData: Record<string, FacetData>, newData: Record<string, FacetData>): Record<string, FacetData> => {
    const facetKeys = [
        'tagsFacet', 'workstreamNameFacet', 'teamNameFacet', 'userFullNameFacet', 'channelFacet', 'directionFacet', 'asIsAbandonedFacet',
        'meetingServiceLevelFacet', 'positiveCsatResponseFacet', 'firstReplyTeamNameFacet', 'firstReplyUserFullNameFacet',
        'lastResolvedTeamNameFacet', 'lastResolvedUserFullNameFacet', 'workstreamChannelFacet', 'workstreamDirectionFacet',
        'typeFacet', 'asIsPrivateCommentFacet', 'asIsPublicCommentFacet', 'asIsStatusChangeFacet', 'asIsReassignFacet',
        'firstReplyUserRolesFacet', 'firstReplyUserTagsFacet', 'firstReplyUserLocationNameFacet', 'lastResolvedUserRolesFacet',
        'lastResolvedUserTagsFacet', 'lastResolvedUserLocationNameFacet', 'userRolesFacet', 'userTagsFacet', 'userLocationFacet'
    ];

    return {
        ...newData,
        ...Object.fromEntries(
            facetKeys.map((key) => [ key, mergeFacets(key, currentData, newData) ])
        )
    };
};

export const updateFacetCountsOnly = (
    oldData: { [facetId: string]: FacetData },
    newData: { [facetId: string]: FacetData },
    targetFacetId: string
): { [facetId: string]: FacetData } => {
    const previousFacet = oldData[targetFacetId];
    const newFacet = newData[targetFacetId];

    if (!previousFacet || !newFacet) return oldData;

    const updatedOptions = previousFacet.options.map((prevOption) => {
        const newOption = newFacet.options.find((opt) => opt.id === prevOption.id);

        const baseOption = {
            ...prevOption,
            count: newOption?.count ?? prevOption.count
        };
        const hasSubBuckets =
            prevOption.subBuckets?.length || newOption?.subBuckets?.length;

        return hasSubBuckets
            ? {
                ...baseOption,
                subBuckets: mergeSubBuckets(
                    prevOption.subBuckets,
                    newOption?.subBuckets
                )
            }
            : baseOption;
    });

    return {
        ...newData,
        [targetFacetId]: {
            ...previousFacet,
            options: updatedOptions
        }
    };
};

export const parseFacetsFromUrl = () => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('view.selected')) return;

        // Primary source: sessionStorage (avoids URL length issues)
        const sessionFacets = loadFacetsFromSession();
        if (sessionFacets) return sessionFacets;

        // Backward compatibility: fall back to URL if sessionStorage is empty
        const facetsParam = urlParams.get('facets');
        if (!facetsParam) return null;

        return JSON.parse(facetsParam);
    } catch (e) {
        logger.error('Failed to parse facets', e);
        return null;
    }
};
